//@flow
import React, {useState} from 'react';
import SwitchableSection from '../../../../../elements/SwitchableSection';
import {Button, makeStyles, Divider} from '@material-ui/core';
import {Add} from '@material-ui/icons';
import {colors} from '../../../../../assets/styles/colors';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import HeaderBannerForm from './HeaderBannerForm';
import BannerItem from './BannerItem';
import {
  useHeaderBannerActions,
  useInitializeHeaderBanner,
} from '../../../utils/header-banner';
import ConfirmModal from '../../../../../elements/Modals/ConfirmModal';

import type {HeaderBanner as HeaderBannerType} from '../../../../../entities';
import {useIsFeatureInTier} from '../../../../../utils/component';
import {useTierRestrictionActions} from '../../../../TierRestriction/utils';

const useStyles = makeStyles(theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    borderRadius: '.75rem',
    width: '90vw',
    maxWidth: '900px',
  },
  addBannerButton: {
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    color: colors.grey500,
    border: `2px dashed ${colors.grey500}`,
    borderRadius: '.75rem',
    paddingBlock: '.85rem',
  },
  divider: {
    marginBlock: '.5rem',
  },
  bannerList: {
    display: 'flex',
    gap: '.5rem',
    flexDirection: 'column',
    marginTop: '.5rem',

    '&[data-disabled=true]': {
      opacity: 0.5,
      pointerEvents: 'none',
    },
  },
}));

export default function HeaderBanner() {
  const [modalOpen, setModalOpen] = useState(false);

  const classes = useStyles();

  const {headerBanners} = useInitializeHeaderBanner();
  const {deleteHeaderBanner, enabledHeaderBanner} = useHeaderBannerActions();

  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const [bannerToEdit, setBannerToEdit] = useState<null | HeaderBannerType>(
    null
  );

  const hasBannerFeatured = useIsFeatureInTier('STORE_HEADER');
  const {showTierRestrictionModal} = useTierRestrictionActions();

  const openModal = () => {
    if (!hasBannerFeatured) {
      return showTierRestrictionModal();
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setBannerToEdit(null);
  };

  return (
    <SwitchableSection
      id="header-banner"
      title="Mensaje Header"
      subtitle="Selecciona el header que saldrá en tu página web"
      chipLabel={hasBannerFeatured ? 'Nuevo' : 'PRO +'}
      hideSwitch
    >
      <Button fullWidth className={classes.addBannerButton} onClick={openModal}>
        <Add />
        Agregar nuevo mensaje
      </Button>

      <section
        className={classes.bannerList}
        data-disabled={!hasBannerFeatured}
      >
        {headerBanners.map((banner, index) => {
          return (
            <>
              {index !== 0 && <Divider className={classes.divider} />}
              <BannerItem
                key={banner.id}
                banner={banner}
                onDelete={() => {
                  setIdToDelete(banner.id);
                }}
                onToggleEnabled={enabled => {
                  enabledHeaderBanner(banner.id, enabled);
                }}
                onEdit={() => {
                  setBannerToEdit(banner);
                  openModal();
                }}
              />
            </>
          );
        })}
      </section>

      {modalOpen && (
        <CustomModal
          open={modalOpen}
          onClose={closeModal}
          classes={{
            paper: classes.modal,
          }}
        >
          <HeaderBannerForm
            banner={bannerToEdit || undefined}
            onSubmitSuccess={closeModal}
          />
        </CustomModal>
      )}

      <ConfirmModal
        open={!!idToDelete}
        title="¿Estás seguro de eliminar este banner?"
        message="Una vez eliminado, no podrás recuperarlo."
        onCancel={() => setIdToDelete(null)}
        onClose={() => setIdToDelete(null)}
        onConfirm={() => {
          if (idToDelete) {
            deleteHeaderBanner(idToDelete);
            setIdToDelete(null);
          }
        }}
      />
    </SwitchableSection>
  );
}
